import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { Calendar as CalIcon, Sparkles, ShieldCheck, Pencil, X } from "lucide-react";
import { imgUrl, API } from "@/config";
import { formatSlot } from "@/utils/slotLabel";

/**
 * OrderSummary — right-column rental summary card. Renders the trip
 * dates + destination, line items, and the running quote breakdown
 * (subtotal / delivery / tax / DP / last-minute / promo / total).
 */
export const OrderSummary = ({ products, quote, trip, zones, reservationId, setQuote, onTripSaved, addressLocked }) => {
  const items = (quote?.line_items || []).map((li) => {
    const p = products.find((x) => x.id === li.product_id);
    return { ...li, image: p?.image };
  });
  const [editing, setEditing] = useState(false);
  return (
    <aside className="glass-warm p-5 sm:p-7 lg:sticky lg:top-24 h-max order-first lg:order-none" data-testid="checkout-summary">
      <div className="flex items-center gap-2 mb-5">
        <Sparkles className="w-4 h-4 text-[#FFD166]" />
        <span className="eyebrow">Rental Summary</span>
      </div>

      <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-4 mb-5 relative" data-testid="checkout-trip-summary">
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="absolute top-2 right-2 rounded-full border border-[#FFD166]/40 text-[#FFD166] text-[10px] uppercase tracking-wider px-2 py-1 flex items-center gap-1 hover:bg-[#FFD166]/10"
          data-testid="checkout-edit-trip-btn"
          title="Change dates or delivery address without leaving checkout"
        >
          <Pencil className="w-3 h-3" /> Edit
        </button>
        <div className="flex items-center gap-2 mb-1">
          <CalIcon className="w-4 h-4 text-[#FFD166]" />
          <div className="text-sm text-[#F6F1E4]">
            <span className="text-[#FFD166] font-medium">{format(trip.range.from, "MMM d")}</span> →{" "}
            <span className="text-[#FFD166] font-medium">{format(trip.range.to, "MMM d, yyyy")}</span>
            <span className="text-[#B9C8D5] ml-1">· {quote?.days || 0} days</span>
          </div>
        </div>
        <div className="text-[11px] text-[#B9C8D5] pl-6">
          Deliver to {trip.delivery_resort}
          {trip.differentPickup && trip.pickup_resort && (
            <> · Pickup <span className="text-[#FFD166]">{trip.pickup_resort}</span></>
          )}
          <br />
          <span className="text-[10px]">
            Delivery {trip.deliverySlot ? formatSlot(trip.deliverySlot) : ""} · Pickup {trip.pickupSlot ? formatSlot(trip.pickupSlot) : ""}
          </span>
        </div>
      </div>

      <div className="space-y-3">
        {items.map((li) => (
          <div key={li.product_id} className="flex items-center gap-3 pb-3 border-b border-[#FFD166]/10" data-testid={`summary-item-${li.product_id}`}>
            {li.image && <img src={imgUrl(li.image)} alt={li.name} className="w-14 h-14 rounded-lg object-cover" />}
            <div className="flex-1 min-w-0">
              <div className="font-serif text-base leading-tight truncate">{li.name}</div>
              {li.addon_for && (
                <div className="text-[10px] text-[#FFD166]" data-testid={`summary-item-${li.product_id}-addon-for`}>
                  add-on for {items.find((x) => x.product_id === li.addon_for)?.name || "your item"}
                </div>
              )}
              <div className="text-[11px] text-[#B9C8D5]">{li.quantity}× · ${li.unit_rate_per_day}/day · {li.days}d</div>
              {li.promo_free_qty > 0 && (
                <div className="text-[10px] text-emerald-400" data-testid={`summary-item-${li.product_id}-free`}>
                  🎁 {li.promo_free_qty} free · {li.promo_label || "promotion"}
                </div>
              )}
            </div>
            {li.line_discount > 0 ? (
              <div className="text-right">
                <div className="text-[11px] text-[#B9C8D5] line-through">${li.subtotal.toFixed(2)}</div>
                <div className="text-sm font-medium text-emerald-400" data-testid={`summary-item-${li.product_id}-after-discount`}>${Number(li.subtotal_after_discount || 0).toFixed(2)}</div>
              </div>
            ) : (
              <div className="text-sm font-medium text-[#F6F1E4]">${li.subtotal.toFixed(2)}</div>
            )}
          </div>
        ))}
      </div>

      {quote && (
        <div className="mt-4 space-y-2 text-sm" data-testid="checkout-quote">
          <div className="flex justify-between text-[#B9C8D5]"><span>Subtotal ({quote.days} days)</span><span>${quote.subtotal.toFixed(2)}</span></div>
          {quote.promo_discount > 0 && (
            <div className="flex justify-between text-emerald-400" data-testid="checkout-promo-line">
              <span data-testid="checkout-promo-label">{quote.partner_code ? `🤝 Partner discount · ${quote.partner_name || quote.partner_code}` : `✨ Firefly Wishes (${quote.promo_code})`}</span>
              <span>−${quote.promo_discount.toFixed(2)}</span>
            </div>
          )}
          {Number(quote.auto_promo_discount || 0) > 0 && quote.applied_promo && (
            <div className="flex justify-between text-[#FFD166]" data-testid="checkout-auto-promo-line">
              <span>🎁 {quote.applied_promo.checkout_label || quote.applied_promo.title}</span>
              <span>−${Number(quote.auto_promo_discount).toFixed(2)}</span>
            </div>
          )}
          {Number(quote.byob_discount || 0) > 0 && (
            <div className="flex justify-between text-emerald-400" data-testid="checkout-byob-line">
              <span>🧸 Build-Your-Own Bundle ({Number(quote.byob_percent || 0)}% off baby gear)</span>
              <span>−${Number(quote.byob_discount).toFixed(2)}</span>
            </div>
          )}
          {(Number(quote.promo_discount || 0) + Number(quote.auto_promo_discount || 0) + Number(quote.byob_discount || 0)) > 0 && (
            <div className="flex justify-between text-[#F6F1E4] pt-1 border-t border-[#FFD166]/10" data-testid="checkout-taxable-subtotal">
              <span>Taxable subtotal</span>
              <span>${Number(quote.taxable_subtotal ?? Math.max(0, quote.subtotal - (quote.promo_discount || 0) - (quote.auto_promo_discount || 0) - (quote.byob_discount || 0))).toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between text-[#B9C8D5]"><span>Delivery</span><span>{quote.delivery_fee === 0 ? "FREE" : `$${quote.delivery_fee.toFixed(2)}`}</span></div>
          {quote.tax > 0 && (
            <div className="flex justify-between text-[#B9C8D5]" data-testid="checkout-tax">
              <span>Sales tax ({(quote.tax_rate * 100).toFixed(1)}% · FL{quote.taxable_subtotal != null && quote.taxable_subtotal !== quote.subtotal ? ` on $${Number(quote.taxable_subtotal).toFixed(2)}` : ""})</span>
              <span>${quote.tax.toFixed(2)}</span>
            </div>
          )}
          {quote.damage_protection_fee > 0 && (
            <div className="flex justify-between text-emerald-300" data-testid="checkout-dp-line">
              <span>Damage Protection</span><span>+${quote.damage_protection_fee.toFixed(2)}</span>
            </div>
          )}
          {quote.enchanted_care_fee > 0 && (
            <div className="flex justify-between text-emerald-300" data-testid="checkout-care-line">
              <span>Enchanted Care</span><span>+${quote.enchanted_care_fee.toFixed(2)}</span>
            </div>
          )}
          {quote.enchanted_pom_fee > 0 && (
            <div className="flex justify-between text-[#FFD166]" data-testid="checkout-pom-line">
              <span>Enchanted Peace of Mind</span><span>+${quote.enchanted_pom_fee.toFixed(2)}</span>
            </div>
          )}
          {quote.last_minute_fee > 0 && (
            <div className="flex justify-between text-[#B9C8D5]" data-testid="checkout-last-minute-line">
              <span className="flex flex-col">
                <span>Last-minute surcharge</span>
                {quote.last_minute_hours_until != null && (
                  <span className="text-[10px] text-[#8FA6B8] mt-0.5">
                    {quote.last_minute_hours_until < 1
                      ? `${Math.round(quote.last_minute_hours_until * 60)}m before delivery`
                      : `${Number(quote.last_minute_hours_until).toFixed(1)}h before delivery`}
                  </span>
                )}
              </span>
              <span>+${quote.last_minute_fee.toFixed(2)}</span>
            </div>
          )}
          {Number(quote.tip_amount || 0) > 0 && (
            <div className="flex justify-between text-emerald-300" data-testid="checkout-tip-line">
              <span>Tip (not taxed)</span>
              <span>+${Number(quote.tip_amount).toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between text-2xl font-serif text-[#FFD166] pt-3 border-t border-[#FFD166]/15">
            <span>Total</span><span data-testid="checkout-total">${quote.total.toFixed(2)}</span>
          </div>
        </div>
      )}

      <p className="text-xs text-[#B9C8D5] mt-4 text-center flex items-center justify-center gap-1">
        <ShieldCheck className="w-3 h-3" /> Secured by Stripe
      </p>

      {editing && (
        <EditTripModal
          trip={trip}
          reservationId={reservationId}
          addressLocked={addressLocked}
          onClose={() => setEditing(false)}
          onSaved={(q, patch) => {
            if (q) setQuote?.(q);
            if (onTripSaved) onTripSaved(patch);
            setEditing(false);
          }}
        />
      )}
    </aside>
  );
};

/* ---------- Inline Edit Trip modal ---------- */
const EditTripModal = ({ trip, reservationId, addressLocked, onClose, onSaved }) => {
  const isoFromDate = (d) => (d ? new Date(d).toISOString().slice(0, 10) : "");
  const [delivery, setDelivery] = useState(isoFromDate(trip.range?.from));
  const [pickup, setPickup] = useState(isoFromDate(trip.range?.to));
  const [deliverySlot, setDeliverySlot] = useState(trip.deliverySlot || "");
  const [pickupSlot, setPickupSlot] = useState(trip.pickupSlot || "");
  const [deliveryResort, setDeliveryResort] = useState(trip.delivery_resort || "");
  const [pickupResort, setPickupResort] = useState(trip.pickup_resort || "");
  const [timeSlots, setTimeSlots] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/time-slots`).then((r) => setTimeSlots(r.data.slots || [])).catch(() => {});
  }, []);

  const submit = async () => {
    if (!delivery || !pickup) return toast.error("Pick both delivery and pickup dates.");
    if (pickup <= delivery) return toast.error("Pickup must be after delivery.");
    setBusy(true);
    try {
      const body = {
        reservation_id: reservationId,
        delivery_date: delivery,
        pickup_date: pickup,
        ...(deliverySlot && { delivery_slot: deliverySlot }),
        ...(pickupSlot && { pickup_slot: pickupSlot }),
      };
      if (!addressLocked) {
        body.delivery_resort = deliveryResort || undefined;
        if (trip.differentPickup) body.pickup_resort = pickupResort || undefined;
      }
      const r = await axios.post(`${API}/checkout/update`, body);
      toast.success("Trip updated ✨");
      onSaved(r.data?.quote, { delivery, pickup, deliveryResort, pickupResort, deliverySlot, pickupSlot });
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || "Couldn't update the trip";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const slotSelect = (label, value, setter, testId) => (
    <label className="block text-xs">
      <span className="eyebrow text-[10px]">{label}</span>
      {timeSlots.length > 0 ? (
        <select
          value={value}
          onChange={(e) => setter(e.target.value)}
          className="input mt-1 w-full"
          data-testid={testId}
        >
          <option value="">Any time</option>
          {timeSlots.map((s) => (
            <option key={s.id} value={s.id}>{s.label}</option>
          ))}
        </select>
      ) : (
        <input type="text" value={value} onChange={(e) => setter(e.target.value)} className="input mt-1" placeholder="Loading…" data-testid={testId} />
      )}
    </label>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="edit-trip-modal">
      <div className="glass max-w-md w-full p-6 relative" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={onClose} className="absolute top-3 right-3 text-[#FFD166]" data-testid="edit-trip-close">
          <X className="w-5 h-5" />
        </button>
        <h3 className="font-serif text-2xl text-[#FFD166] mb-1">Edit your trip</h3>
        <p className="text-xs text-[#B9C8D5] mb-4">
          {addressLocked
            ? "Change your delivery and pickup dates or time windows. We'll re-check availability and update your price."
            : "Change dates, time windows, or delivery address before you pay."
          }
        </p>

        {/* Dates */}
        <div className="grid grid-cols-2 gap-3 mb-3">
          <label className="text-xs">
            <span className="eyebrow text-[10px]">Delivery date</span>
            <input type="date" value={delivery} onChange={(e) => setDelivery(e.target.value)} className="input mt-1" data-testid="edit-trip-delivery-date" />
          </label>
          <label className="text-xs">
            <span className="eyebrow text-[10px]">Pickup date</span>
            <input type="date" value={pickup} onChange={(e) => setPickup(e.target.value)} className="input mt-1" data-testid="edit-trip-pickup-date" />
          </label>
        </div>

        {/* Time windows */}
        <div className="grid grid-cols-2 gap-3 mb-3">
          {slotSelect("Delivery window", deliverySlot, setDeliverySlot, "edit-trip-delivery-slot")}
          {slotSelect("Pickup window", pickupSlot, setPickupSlot, "edit-trip-pickup-slot")}
        </div>

        {/* Address (non-phone bookings only) */}
        {!addressLocked && (
          <>
            <label className="block mt-1 mb-3 text-xs">
              <span className="eyebrow text-[10px]">Delivery address / resort</span>
              <input type="text" value={deliveryResort} onChange={(e) => setDeliveryResort(e.target.value)} className="input mt-1" data-testid="edit-trip-delivery-resort" />
            </label>
            {trip.differentPickup && (
              <label className="block mb-3 text-xs">
                <span className="eyebrow text-[10px]">Pickup address / resort</span>
                <input type="text" value={pickupResort} onChange={(e) => setPickupResort(e.target.value)} className="input mt-1" data-testid="edit-trip-pickup-resort" />
              </label>
            )}
          </>
        )}

        {/* Locked address display */}
        {addressLocked && trip.delivery_resort && (
          <div className="mb-3 rounded-lg border border-[#FFD166]/20 bg-[#FFD166]/5 px-3 py-2 text-xs text-[#B9C8D5]" data-testid="edit-trip-address-locked">
            <span className="text-[#FFD166] font-medium">Delivery to:</span> {trip.delivery_resort}
            <span className="block text-[10px] mt-0.5 text-[#B9C8D5]/60">Address is set by Enchanted Gear Rentals and cannot be changed here.</span>
          </div>
        )}

        <button type="button" onClick={submit} disabled={busy} className="btn-primary w-full mt-2" data-testid="edit-trip-save">
          {busy ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
};

export default OrderSummary;
